import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import LotteryCard from "./LotteryCard";
import { toast } from "react-hot-toast";

/* ───────── utils ───────── */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
const currencySymbol = (c) =>
  ({ USD: "$", EUR: "€", GBP: "£", INR: "₹", LKR: "Rs" }[c] || c || "$");

export default function OfferLanding({ user }) {
  const q = useQuery();
  const navigate = useNavigate();

  const agentUid = q.get("agent"); // required
  const cardStr = q.get("cards") || ""; // optional (comma separated)
  const cardIdsParam = cardStr ? cardStr.split(",") : [];

  const [cards, setCards] = useState([]);
  const [agentDoc, setAgentDoc] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedMap, setSelectedMap] = useState({});
  const [phone, setPhone] = useState("");

  // Load agent + cards
  useEffect(() => {
    (async () => {
      if (!agentUid) return;

      // 1) Agent (for agentName and future surface)
      const aSnap = await getDoc(doc(db, "agents", agentUid));
      setAgentDoc(aSnap.exists() ? { id: aSnap.id, ...aSnap.data() } : null);

      // 2) Cards (by id list or all active)
      const list = [];
      if (cardIdsParam.length) {
        for (const id of cardIdsParam) {
          const snap = await getDoc(doc(db, "cards", id));
          if (snap.exists() && snap.data().status === "active") {
            list.push({ id: snap.id, ...snap.data() });
          }
        }
      } else {
        const qy = query(
          collection(db, "cards"),
          where("agentUid", "==", agentUid),
          where("status", "==", "active")
        );
        const snap = await getDocs(qy);
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      }
      setCards(list);
    })();
  }, [agentUid, cardStr]);

  // Prefill phone from user doc (if logged in)
  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const u = await getDoc(doc(db, "users", user.uid));
      if (u.exists()) {
        const d = u.data();
        // prefer phone or mobile
        setPhone(d.phone || d.mobile || "");
      }
    })();
  }, [user?.uid]);

  // If user is logged in and has no referredByAgent, set it to this agent
  useEffect(() => {
    (async () => {
      if (!user?.uid || !agentUid) return;
      const uRef = doc(db, "users", user.uid);
      const snap = await getDoc(uRef);
      const d = snap.exists() ? snap.data() : {};
      if (!d.referredByAgent) {
        await setDoc(
          uRef,
          {
            referredByAgent: agentUid,
            // keep createdAt if present
            createdAt: d.createdAt || serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
    })();
  }, [user?.uid, agentUid]);

  const activeCard = cards[activeIdx];

  /* selection handling */
  const onNumberClick = (num) => {
    if (!activeCard) return;
    const filled = activeCard.filled || [];
    const locked = activeCard.locked || [];
    if (filled.includes(num) || locked.includes(num)) return;

    const key = activeCard.id;
    setSelectedMap((prev) => {
      const arr = new Set(prev[key] || []);
      arr.has(num) ? arr.delete(num) : arr.add(num);
      return { ...prev, [key]: [...arr] };
    });
  };

  /* totals */
  const totalCount = Object.values(selectedMap).reduce(
    (s, arr) => s + (arr?.length || 0),
    0
  );
  const currency = cards[0]?.currency || "USD";
  const symbol = currencySymbol(currency);
  const totalPrice = cards.reduce(
    (sum, c) =>
      sum + (selectedMap[c.id]?.length || 0) * (Number(c.pricePerNumber) || 0),
    0
  );

  /* submit */
  const submitRequest = async () => {
    try {
      // 1) Must be logged in
      if (!user?.uid) {
        toast.error("Please login first.");
        sessionStorage.setItem(
          "postLoginRedirect",
          window.location.pathname + window.location.search
        );
        return navigate("/login");
      }

      // 2) Phone required
      if (!phone?.trim()) return toast.error("Enter your mobile number.");

      // 3) Must pick at least one number overall
      if (!totalCount) return toast("Pick at least one number.");

      // 4) Ensure user has referral saved (if still missing for some reason)
      const uRef = doc(db, "users", user.uid);
      const uSnap = await getDoc(uRef);
      const uData = uSnap.exists() ? uSnap.data() : {};
      if (!uData.referredByAgent && agentUid) {
        await setDoc(
          uRef,
          {
            referredByAgent: agentUid,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      // 5) Save phone/mobile back to user for future prefill
      await setDoc(
        uRef,
        {
          phone: phone.trim(),
          mobile: phone.replace(/\D/g, ""), // normalized
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // 6) Build tickets per selected card
      const batchCards = cards.filter(
        (c) => (selectedMap[c.id]?.length || 0) > 0
      );

      const agentName =
        agentDoc?.username ||
        agentDoc?.fullName ||
        agentDoc?.id?.slice(0, 6) ||
        "";

      for (const c of batchCards) {
        const unitPrice = Number(c.pricePerNumber || 0.5);
        const picks = selectedMap[c.id];

        await addDoc(collection(db, "tickets"), {
          status: "requested",
          requestedAt: serverTimestamp(),
          createdAt: serverTimestamp(),

          // ownership / context
          cardId: c.id,
          agentUid,
          agentName, // helpful label
          referredByAgent: agentUid, // <-- attribute to this agent

          // player
          userUid: user.uid,
          userName: user.displayName || user.email || user.uid.slice(0, 6),
          phone: phone.trim(), // keep formatted
          userMobile: phone.trim(), // staying backward compatible with your other pages

          // numbers & pricing
          numbers: picks,
          currency: c.currency || "USD",
          unitPrice,
          amount: Number((unitPrice * picks.length).toFixed(2)),

          // origin
          referrer: "offer_landing",
        });
      }

      toast.success("Request submitted. Wait for agent approval.");
      navigate("/my-tickets");
    } catch (e) {
      console.error(e);
      toast.error("Request failed. Try again.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-8 md:mt-16">
      <h1 className="text-2xl font-bold mb-2">Lottery Offer</h1>
      {!cards.length && (
        <div className="text-slate-300">No active cards in this offer.</div>
      )}

      {activeCard && (
        <>
          {cards.length > 1 && (
            <div className="flex items-center gap-2 mb-3">
              <button
                disabled={activeIdx === 0}
                onClick={() => setActiveIdx((i) => i - 1)}
                className="px-3 py-1 rounded bg-slate-700 disabled:opacity-50"
              >
                Prev
              </button>
              <div className="text-sm text-slate-300">
                Card {activeIdx + 1} of {cards.length} · #
                {activeCard.id.slice(0, 8)}…
              </div>
              <button
                disabled={activeIdx === cards.length - 1}
                onClick={() => setActiveIdx((i) => i + 1)}
                className="px-3 py-1 rounded bg-slate-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}

          <LotteryCard
            card={activeCard}
            selected={selectedMap[activeCard.id] || []}
            onNumberClick={onNumberClick}
          />

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-300">
              Selected: <b>{totalCount}</b> · Total:{" "}
              <b>
                {currencySymbol(cards[0]?.currency || "USD")}
                {totalPrice.toFixed(2)}
              </b>
            </div>
            <button
              className="bg-lime-400 text-black px-4 py-2 rounded-2xl font-semibold"
              onClick={submitRequest}
            >
              Submit Request
            </button>
          </div>

          <div className="mt-4 p-4 rounded-xl bg-slate-800">
            <label className="text-sm text-slate-300">
              Mobile number (for verification & payment)
            </label>
            <input
              className="w-full mt-2 px-3 py-2 rounded bg-slate-700 text-white"
              placeholder="07X XXX XXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="text-xs text-slate-400 mt-1">
              We’ll show this to the agent with your request.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
